import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container, Typography, Grid, Card, CardContent, CardMedia, Button,
  Pagination, Box, Chip, CircularProgress, Alert, Tabs, Tab, Paper,
  FormControl, InputLabel, Select, MenuItem, TextField, Snackbar,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Popover,
  Checkbox, FormControlLabel,
} from '@mui/material';
import {
  KeyboardArrowDown, ViewList, ExpandMore, OpenInNew, ArrowUpward,
  ArrowDownward, ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';
import DashboardLayout from '../../components/DashboardLayout';
import BlogDialog from '../../components/BlogDialog';
import {
  getBlogPosts, createPreBuiltMergedBlog, getAllPreBuiltMergedBlogs,
  getBlogsBySubcategory, generateDynamicMergedBlog, getMyBlogPosts,
  getBlogPerformanceStats, updateBlogPost, getBlogPostById
} from '../../services/blogService';
import categoryService from '../../services/categoryService';
import { useAuth } from '../../context/AuthContext';
import { BlogContext } from '../BlogContext';
import BlogPostsTab from './tabs/BlogPostsTab';
import MergedBlogsTab from './tabs/MergedBlogsTab';
import DateRangePicker from './DateRangePicker';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const { isAuthenticated, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Blog Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'view', 'edit'
  const [selectedPost, setSelectedPost] = useState(null);

  // Deep link: /blog?view=<id> opens that post rendered (read-only), and
  // /blog?edit=<id> opens it in the editor (e.g. from Account Automation's
  // "View blog" link). Works for drafts and published posts.
  // Runs once per mount via a ref guard; we deliberately do NOT rewrite the URL
  // (rewriting re-runs this effect and cancels the in-flight fetch).
  const [searchParams] = useSearchParams();
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    const viewId = searchParams.get('view');
    const editId = searchParams.get('edit');
    const targetId = viewId || editId;
    if (!targetId || deepLinkHandledRef.current) return;
    deepLinkHandledRef.current = true;
    (async () => {
      try {
        const post = await getBlogPostById(targetId);
        if (post) {
          setDialogMode(viewId ? 'view' : 'edit');
          setSelectedPost(post);
          setDialogOpen(true);
        }
      } catch (e) {
        // ignore — invalid/removed id
      }
    })();
  }, [searchParams]);

  // Tab state - default to Pre-built Blog tab (1) for admin portal
  const [tabValue, setTabValue] = useState(isAdmin ? 1 : 0);

  // Pre-built blog state
  const [categories, setCategories] = useState([]);
  const [preBuiltCategory, setPreBuiltCategory] = useState('');
  const [preBuiltSubcategory, setPreBuiltSubcategory] = useState('');
  // Removed blogMergeLimit - all blogs in subcategory are now included for management
  const [rankingCriteria, setRankingCriteria] = useState('views');
  const [preBuiltLoading, setPreBuiltLoading] = useState(false);
  const [mergedBlogs, setMergedBlogs] = useState([]);
  const [loadingMergedBlogs, setLoadingMergedBlogs] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]); // Store all blog posts for merged blog display
  const [blogPerformanceData, setBlogPerformanceData] = useState({}); // Map of blogId -> performance data
  const [mergedBlogPerformanceData, setMergedBlogPerformanceData] = useState({}); // Map of mergedBlogId -> performance data
  const [blogPageStats, setBlogPageStats] = useState({}); // Map of blogId -> page-level stats array
  const [sourceBlogMap, setSourceBlogMap] = useState({}); // Map of blogId -> full blog object with content
  const [pausedBlogs, setPausedBlogs] = useState({}); // Map of mergedBlogId -> Set of paused blog IDs
  const [expandedStatsDialog, setExpandedStatsDialog] = useState(null); // { blogId, blogTitle, offerStats, views, clicks, sales, revenue } or null
  const [mergedBlogsLimit, setMergedBlogsLimit] = useState(3); // Limit for merged blogs display (horizontal)
  const [blogsPerMergedBlogLimit, setBlogsPerMergedBlogLimit] = useState({}); // Map of mergedBlogId -> limit for blogs within that merged blog (vertical)
  const [mergedBlogsStartDate, setMergedBlogsStartDate] = useState(''); // Start date for filtering merged blogs
  const [mergedBlogsEndDate, setMergedBlogsEndDate] = useState(''); // End date for filtering merged blogs
  const [dateRangePickerOpen, setDateRangePickerOpen] = useState(false);
  const [dateRangePickerAnchor, setDateRangePickerAnchor] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [startMonth, setStartMonth] = useState(new Date());
  const [endMonth, setEndMonth] = useState(new Date());
  const [includeToday, setIncludeToday] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Load categories for prebuilt blog tab
  useEffect(() => {
    if (isAdmin && tabValue === 1) {
      const loadCategories = async () => {
        try {
          const categoriesData = await categoryService.getCategoriesForSelect();
          setCategories(categoriesData);
        } catch (error) {
          console.error('Error loading categories:', error);
        }
      };
      loadCategories();
    }
  }, [isAdmin, tabValue]);

  // Load merged blogs when prebuilt tab is active
  useEffect(() => {
    if (isAdmin && tabValue === 1) {
      fetchMergedBlogs();
    }
  }, [isAdmin, tabValue]);

  useEffect(() => {
    // Only fetch blog posts if not admin or if tab 0 is active (for non-admin users)
    if (!isAdmin && tabValue === 0) {
      const fetchPosts = async () => {
        try {
          setLoading(true);
          const data = await getBlogPosts(page, 6, selectedTag);
          setPosts(data.posts);
          setTotalPages(data.totalPages);
          setLoading(false);
        } catch (err) {
          console.error('Error fetching blog posts:', err);
          setError('Failed to load blog posts. Please try again later.');
          setLoading(false);
        }
      };

      fetchPosts();
    }
  }, [page, selectedTag, tabValue, isAdmin]);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo(0, 0);
  };

  const handleTagClick = (tag) => {
    setSelectedTag(tag === selectedTag ? null : tag);
    setPage(1);
  };

  const handleCreatePost = () => {
    setDialogMode('create');
    setSelectedPost(null);
    setDialogOpen(true);
  };

  const handleViewPost = (post) => {
    setDialogMode('view');
    setSelectedPost(post);
    setDialogOpen(true);
  };

  const handleEditPost = (post) => {
    setDialogMode('edit');
    setSelectedPost(post);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPost(null);
  };

  const handleDialogSuccess = async () => {
    // Refresh posts after create/edit
    try {
      const data = await getBlogPosts(page, 6, selectedTag);
      setPosts(data.posts);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error('Error refreshing posts:', err);
    }
  };

  // Pre-built blog functions
  const handlePreBuiltCategoryChange = (event) => {
    const categoryId = event.target.value;
    setPreBuiltCategory(categoryId);
    setPreBuiltSubcategory('');
  };

  const handlePreBuiltSubcategoryChange = (event) => {
    const subcategoryId = event.target.value;
    setPreBuiltSubcategory(subcategoryId);
  };

  // Reload a single merged blog
  const reloadMergedBlog = async (mergedBlogId) => {
    try {
      const mergedBlogIdStr = mergedBlogId?.toString();
      if (!mergedBlogIdStr) return;

      const updatedBlog = await getBlogPostById(mergedBlogIdStr);

      setMergedBlogs(prev => prev.map(mb =>
        mb._id?.toString() === mergedBlogIdStr ? updatedBlog : mb
      ));

      if (updatedBlog.pausedBlogIds && Array.isArray(updatedBlog.pausedBlogIds) && updatedBlog.pausedBlogIds.length > 0) {
        setPausedBlogs(prev => ({
          ...prev,
          [mergedBlogIdStr]: new Set(
            updatedBlog.pausedBlogIds.map(id => {
              const idStr = typeof id === 'object' ? id._id || id : id;
              return idStr?.toString();
            }).filter(Boolean)
          )
        }));
      } else {
        setPausedBlogs(prev => {
          const newState = { ...prev };
          if (newState[mergedBlogIdStr]) {
            delete newState[mergedBlogIdStr];
          }
          return newState;
        });
      }

      if (updatedBlog.sourceBlogIds && Array.isArray(updatedBlog.sourceBlogIds)) {
        const sourceBlogPromises = updatedBlog.sourceBlogIds.map(async (id) => {
          const blogId = typeof id === 'object' ? id._id || id : id;
          const blogIdStr = blogId?.toString();
          if (!blogIdStr) return null;

          if (sourceBlogMap[blogIdStr]) {
            return null;
          }

          try {
            const blog = await getBlogPostById(blogIdStr);
            return { blogId: blogIdStr, blog };
          } catch (error) {
            console.error(`Error fetching source blog ${blogIdStr}:`, error);
            return null;
          }
        });

        const sourceBlogResults = await Promise.all(sourceBlogPromises);
        const newSourceBlogMap = { ...sourceBlogMap };
        sourceBlogResults.forEach((result) => {
          if (result && result.blog) {
            newSourceBlogMap[result.blogId] = result.blog;
          }
        });

        if (Object.keys(newSourceBlogMap).length > Object.keys(sourceBlogMap).length) {
          setSourceBlogMap(newSourceBlogMap);
        }
      }
    } catch (error) {
      console.error('Error reloading merged blog:', error);
      showSnackbar('Failed to reload snake', 'error');
    }
  };

  // Fetch all merged blogs (both from BlogPost and PreBuiltMergedBlog collections)
  const fetchMergedBlogs = async () => {
    setLoadingMergedBlogs(true);
    try {
      // Fetch merged blogs from BlogPost collection (legacy)
      // Note: getMyBlogPosts already includes pre-built merged blogs, so we need to deduplicate
      const postsData = await getMyBlogPosts({ limit: 100 });
      const mergedFromBlogPost = (postsData.posts || []).filter(post => post.isMergedBlog && !post.isPreBuiltMergedBlog);

      // Fetch pre-built merged blogs from PreBuiltMergedBlog collection
      let preBuiltMerged = [];
      try {
        const preBuiltData = await getAllPreBuiltMergedBlogs();
        preBuiltMerged = preBuiltData.blogs || preBuiltData || [];
        preBuiltMerged = preBuiltMerged.map(blog => ({
          ...blog,
          isMergedBlog: true,
          isPreBuiltMergedBlog: true
        }));
      } catch (error) {
        console.warn('Error fetching pre-built merged blogs:', error);
      }

      // Combine and deduplicate by _id to avoid duplicates
      const mergedMap = new Map();

      // Add legacy merged blogs from BlogPost
      mergedFromBlogPost.forEach(blog => {
        const id = blog._id?.toString();
        if (id) {
          mergedMap.set(id, blog);
        }
      });

      // Add pre-built merged blogs (will overwrite if duplicate ID exists)
      preBuiltMerged.forEach(blog => {
        const id = blog._id?.toString();
        if (id) {
          mergedMap.set(id, blog);
        }
      });

      // Convert map back to array
      const merged = Array.from(mergedMap.values());

      setMergedBlogs(merged);

      // Initialize paused blogs state from merged blogs
      const initialPausedBlogs = {};
      merged.forEach(mergedBlog => {
        if (mergedBlog.pausedBlogIds && Array.isArray(mergedBlog.pausedBlogIds) && mergedBlog.pausedBlogIds.length > 0) {
          const mergedBlogIdStr = mergedBlog._id?.toString();
          initialPausedBlogs[mergedBlogIdStr] = new Set(
            mergedBlog.pausedBlogIds.map(id => {
              const idStr = typeof id === 'object' ? id._id || id : id;
              return idStr?.toString();
            }).filter(Boolean)
          );
        }
      });
      if (Object.keys(initialPausedBlogs).length > 0) {
        setPausedBlogs(initialPausedBlogs);
      }

      // Collect all unique source blog IDs
      const allSourceBlogIds = new Set();
      merged.forEach(mergedBlog => {
        if (mergedBlog.sourceBlogIds && Array.isArray(mergedBlog.sourceBlogIds)) {
          mergedBlog.sourceBlogIds.forEach(id => {
            const blogId = typeof id === 'object' ? id._id || id : id;
            if (blogId) allSourceBlogIds.add(blogId.toString());
          });
        }
      });

      // Fetch all source blog details
      const sourceBlogPromises = Array.from(allSourceBlogIds).map(async (blogId) => {
        try {
          const blog = await getBlogPostById(blogId);
          return { blogId, blog };
        } catch (error) {
          if (error.response?.status === 404) {
            console.warn(`Source blog ${blogId} not found (may have been deleted)`);
          } else {
            console.error(`Error fetching source blog ${blogId}:`, error);
          }
          return { blogId, blog: null };
        }
      });

      const sourceBlogResults = await Promise.all(sourceBlogPromises);
      const newSourceBlogMap = {};
      sourceBlogResults.forEach(({ blogId, blog }) => {
        if (blog) {
          newSourceBlogMap[blogId] = blog;
        }
      });

      setSourceBlogMap(newSourceBlogMap);

      // Update blogPosts state with source blogs for display
      const updatedBlogPosts = [...blogPosts];
      sourceBlogResults.forEach(({ blogId, blog }) => {
        if (blog && !updatedBlogPosts.find(bp => bp._id === blogId)) {
          updatedBlogPosts.push(blog);
        }
      });
      setBlogPosts(updatedBlogPosts);

      // Fetch performance data for each unique affiliate
      const affiliateIds = new Set();
      merged.forEach(mergedBlog => {
        if (mergedBlog.author) {
          const authorId = typeof mergedBlog.author === 'object' ? mergedBlog.author._id || mergedBlog.author : mergedBlog.author;
          if (authorId) affiliateIds.add(authorId.toString());
        }
      });

      sourceBlogResults.forEach(({ blog }) => {
        if (blog && blog.author) {
          const authorId = typeof blog.author === 'object' ? blog.author._id || blog.author : blog.author;
          if (authorId) affiliateIds.add(authorId.toString());
        }
      });

      // Fetch performance data for each affiliate
      const performancePromises = Array.from(affiliateIds).map(async (affiliateId) => {
        try {
          const perfData = await getBlogPerformanceStats(affiliateId);
          return { affiliateId, perfData };
        } catch (error) {
          console.error(`Error fetching performance for affiliate ${affiliateId}:`, error);
          return { affiliateId, perfData: null };
        }
      });

      const performanceResults = await Promise.all(performancePromises);
      const perfMap = {};
      const mergedBlogPerfMap = {};
      const pageStatsMap = {};

      performanceResults.forEach(({ affiliateId, perfData }) => {
        if (perfData && perfData.stats) {
          if (perfData.stats.byBlog) {
            perfData.stats.byBlog.forEach(blogStat => {
              if (blogStat.blogPostId || blogStat.realBlogPostId) {
                const blogIdStr = (blogStat.realBlogPostId || blogStat.blogPostId).toString();
                perfMap[blogIdStr] = blogStat;

                // Also store it under the display ID so that components relying on display ID can find it
                if (blogStat.blogPostId) {
                  perfMap[blogStat.blogPostId.toString()] = blogStat;
                }

                const prebuiltId = blogStat.attachedPrebuiltMergedBlogId;
                if (prebuiltId) {
                  const mergedBlog = merged.find(mb => mb._id?.toString() === prebuiltId);
                  if (mergedBlog) {
                    if (!mergedBlogPerfMap[mergedBlog._id]) {
                      mergedBlogPerfMap[mergedBlog._id] = { count: 0, clicks: 0, sales: 0, totalRevenue: 0 };
                    }
                    mergedBlogPerfMap[mergedBlog._id].count += blogStat.totalViews || 0;
                    // Also fallbacks for legacy references
                    mergedBlogPerfMap[mergedBlog._id].totalViews = mergedBlogPerfMap[mergedBlog._id].count;
                    mergedBlogPerfMap[mergedBlog._id].clicks += blogStat.clicks || 0;
                    mergedBlogPerfMap[mergedBlog._id].sales += blogStat.sales || 0;
                    mergedBlogPerfMap[mergedBlog._id].totalRevenue += blogStat.totalRevenue || 0;
                    mergedBlogPerfMap[mergedBlog._id].revenue = mergedBlogPerfMap[mergedBlog._id].totalRevenue;
                  }
                }
              }
            });
          }

          if (perfData.stats.pageStatsByBlog && Array.isArray(perfData.stats.pageStatsByBlog)) {
            perfData.stats.pageStatsByBlog.forEach(blogPageStat => {
              if ((blogPageStat.blogPostId || blogPageStat.realBlogPostId) && blogPageStat.pages) {
                const blogIdStr = (blogPageStat.realBlogPostId || blogPageStat.blogPostId).toString();
                pageStatsMap[blogIdStr] = blogPageStat.pages;

                if (blogPageStat.blogPostId) {
                  pageStatsMap[blogPageStat.blogPostId.toString()] = blogPageStat.pages;
                }
              }
            });
          }
        }
      });

      setBlogPerformanceData(perfMap);
      setMergedBlogPerformanceData(mergedBlogPerfMap);
      setBlogPageStats(pageStatsMap);
    } catch (error) {
      console.error('Error fetching merged blogs:', error);
      showSnackbar('Failed to load snakes', 'error');
      setMergedBlogs([]);
    } finally {
      setLoadingMergedBlogs(false);
    }
  };

  // Reorder blogs within a merged blog
  const handleReorderBlogsInMerged = async (mergedBlogId, newOrder) => {
    try {
      const mergedBlog = mergedBlogs.find(mb => mb._id === mergedBlogId);
      if (!mergedBlog) {
        showSnackbar('Snake not found', 'error');
        return;
      }

      const mergedBlogIdStr = mergedBlogId?.toString();
      const updatedBlogData = {
        sourceBlogIds: newOrder
      };

      await updateBlogPost(mergedBlogIdStr, updatedBlogData);
      showSnackbar('Blog order updated', 'success');

      await reloadMergedBlog(mergedBlogId);
    } catch (error) {
      console.error('Error reordering blogs in merged blog:', error);
      showSnackbar('Failed to reorder blogs: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  // Remove a blog from a merged blog
  const handleRemoveBlogFromMerged = async (mergedBlogId, sourceBlogId) => {
    try {
      const mergedBlog = mergedBlogs.find(mb => mb._id === mergedBlogId);
      if (!mergedBlog) {
        showSnackbar('Snake not found', 'error');
        return;
      }

      const mergedBlogIdStr = mergedBlogId?.toString();
      const sourceBlogIdStr = typeof sourceBlogId === 'object' ? (sourceBlogId._id || sourceBlogId)?.toString() : sourceBlogId?.toString();

      if (!sourceBlogIdStr) {
        showSnackbar('Invalid blog ID', 'error');
        return;
      }

      const currentSourceBlogIds = mergedBlog.sourceBlogIds || [];
      const updatedSourceBlogIds = currentSourceBlogIds.filter(id => {
        const idStr = typeof id === 'object' ? (id._id || id)?.toString() : id?.toString();
        return idStr !== sourceBlogIdStr;
      });

      // Also remove from pausedBlogIds if present
      const currentPausedBlogIds = mergedBlog.pausedBlogIds || [];
      const updatedPausedBlogIds = currentPausedBlogIds.filter(id => {
        const idStr = typeof id === 'object' ? (id._id || id)?.toString() : id?.toString();
        return idStr !== sourceBlogIdStr;
      });


      const updatedBlogData = {
        sourceBlogIds: updatedSourceBlogIds,
        pausedBlogIds: updatedPausedBlogIds,
        blogCount: updatedSourceBlogIds.length
      };

      await updateBlogPost(mergedBlogIdStr, updatedBlogData);
      showSnackbar('Blog removed from snake', 'success');

      await reloadMergedBlog(mergedBlogId);
    } catch (error) {
      console.error('Error removing blog from merged blog:', error);
      showSnackbar('Failed to remove blog: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const sortBlogsByPerformance = async (blogs, affiliateId, criteria) => {
    try {
      const performanceData = await getBlogPerformanceStats(affiliateId);
      const performanceMap = new Map();

      if (performanceData?.stats?.byBlog) {
        performanceData.stats.byBlog.forEach(blogStat => {
          let blogId = blogStat.blogPostId;
          if (blogId && typeof blogId !== 'string') {
            blogId = blogId.toString();
          }
          if (blogId) {
            const views = blogStat.totalViews || 0;
            const revenue = blogStat.totalRevenue || blogStat.revenue || 0;
            const revenuePerView = views > 0 ? revenue / views : 0;

            performanceMap.set(blogId, {
              views,
              revenue,
              revenuePerView
            });
          }
        });
      }

      return blogs.sort((a, b) => {
        let aId = a._id;
        let bId = b._id;
        if (aId && typeof aId !== 'string') {
          aId = aId.toString();
        }
        if (bId && typeof bId !== 'string') {
          bId = bId.toString();
        }

        const aStats = performanceMap.get(aId);
        const bStats = performanceMap.get(bId);

        if (aStats && bStats) {
          if (criteria === 'revenuePerView') {
            return bStats.revenuePerView - aStats.revenuePerView;
          } else {
            return bStats.views - aStats.views;
          }
        }
        if (aStats && !bStats) return -1;
        if (!aStats && bStats) return 1;
        return Math.random() - 0.5;
      });
    } catch (error) {
      console.error('Error fetching blog performance, randomizing instead:', error);
      return blogs.sort(() => Math.random() - 0.5);
    }
  };

  const concatenateBlogContents = (blogs) => {
    const pages = [];
    let pageId = 1;

    blogs.forEach((blog) => {
      try {
        let blogContent = blog.content;
        if (typeof blogContent === 'string') {
          try {
            blogContent = JSON.parse(blogContent);
          } catch (e) {
            blogContent = [{ id: `text-${pageId}`, type: 'text', content: blogContent }];
          }
        }

        if (Array.isArray(blogContent)) {
          if (blogContent[0]?.elements) {
            blogContent.forEach((page) => {
              if (page.elements && Array.isArray(page.elements)) {
                pages.push({
                  id: pageId++,
                  elements: [...page.elements]
                });
              }
            });
          } else if (blogContent[0]?.type) {
            pages.push({
              id: pageId++,
              elements: blogContent
            });
          }
        }
      } catch (e) {
        console.error('Error processing blog content:', e);
      }
    });

    return JSON.stringify(pages);
  };

  const generatePreBuiltBlog = async () => {
    if (!preBuiltCategory || !preBuiltSubcategory) {
      showSnackbar('Please select both category and subcategory', 'error');
      return;
    }

    setPreBuiltLoading(true);
    try {
      if (preBuiltCategory === 'all' || preBuiltSubcategory === 'all') {
        showSnackbar('Please select a specific category and subcategory to create a snake.', 'error');
        setPreBuiltLoading(false);
        return;
      }

      console.log('🔄 Generating dynamic merged blog server-side:', {
        categoryId: preBuiltCategory,
        subcategoryId: preBuiltSubcategory,
        rankingCriteria,
        note: 'All blogs in subcategory will be included (no limit)'
      });

      // Use server-side dynamic generation endpoint
      const result = await generateDynamicMergedBlog(
        preBuiltCategory,
        preBuiltSubcategory,
        {
          rankingCriteria
        }
      );

      if (result && result.success) {
        console.log('✅ Dynamic merged blog generated successfully:', {
          blogsIncluded: result.blogsIncluded,
          totalPages: result.totalPages,
          isUpdate: result.isUpdate
        });

        const action = result.isUpdate ? 'Updated' : 'Created';
        showSnackbar(
          `${action} snake with ${result.blogsIncluded} blog posts (${result.totalPages} pages)`,
          'success'
        );

        // Refresh merged blogs list
        await fetchMergedBlogs();

        // Reset form
        setPreBuiltCategory('');
        setPreBuiltSubcategory('');
      } else {
        showSnackbar(result?.message || 'Failed to generate snake', 'error');
      }
    } catch (error) {
      console.error('Error generating dynamic merged blog:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      showSnackbar('Failed to generate snake: ' + errorMessage, 'error');
    } finally {
      setPreBuiltLoading(false);
    }
  };

  // Extract all unique tags from posts
  const allTags = [...new Set(posts.flatMap(post => post.tags))];

  const blogActions = isAuthenticated ? (
    <Button
      onClick={handleCreatePost}
      variant="contained"
      startIcon={<AddIcon />}
    >
      New Post
    </Button>
  ) : null;


  const contextValue = {
    isAdmin, posts, page, totalPages, loading, error, selectedTag,
    setSelectedTag, dialogOpen, setDialogOpen, dialogMode, selectedPost,
    tabValue, setTabValue, categories, preBuiltCategory, preBuiltSubcategory,
    rankingCriteria, setRankingCriteria, preBuiltLoading, mergedBlogs,
    loadingMergedBlogs, blogPosts, blogPerformanceData, mergedBlogPerformanceData,
    blogPageStats, sourceBlogMap, pausedBlogs, expandedStatsDialog,
    setExpandedStatsDialog, mergedBlogsLimit, setMergedBlogsLimit,
    blogsPerMergedBlogLimit, setBlogsPerMergedBlogLimit,
    mergedBlogsStartDate, mergedBlogsEndDate,
    dateRangePickerOpen, setDateRangePickerOpen,
    dateRangePickerAnchor, setDateRangePickerAnchor,
    tempStartDate, setTempStartDate, tempEndDate, setTempEndDate,
    startMonth, setStartMonth, endMonth, setEndMonth,
    includeToday, setIncludeToday, snackbar, setSnackbar,
    handleCloseSnackbar, handlePageChange, handleTagClick,
    handleCreatePost, handleViewPost, handleEditPost,
    handleCloseDialog, handleDialogSuccess,
    handlePreBuiltCategoryChange, handlePreBuiltSubcategoryChange,
    fetchMergedBlogs, handleReorderBlogsInMerged, handleRemoveBlogFromMerged,
    isAuthenticated, user,
    showSnackbar, reloadMergedBlog,
    setPausedBlogs,
    setMergedBlogsStartDate, setMergedBlogsEndDate,
    allTags, blogActions,
  };

  return (
    <BlogContext.Provider value={contextValue}>
    <DashboardLayout title="Blog Optimizer" actions={blogActions} maxWidth="lg">
      <Box sx={{ maxWidth: 'lg', mx: 'auto', px: 3, py: 2 }}>

        {/* Tabs */}
        {isAdmin ? (
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>

          </Tabs>
        ) : (
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
            <Tab label="Blog Posts" />
          </Tabs>
        )}

        {!isAdmin && tabValue === 0 && <BlogPostsTab />}


        {isAdmin && tabValue === 1 && <MergedBlogsTab />}

        {/* Admin tabs don't mount BlogPostsTab (which hosts the editor popup),
            so render the editor here for admins — covers the /blog?edit=<id>
            deep link and any admin edit action. */}
        {isAdmin && (
          <BlogDialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            mode={dialogMode}
            blogPost={selectedPost}
            onSuccess={handleDialogSuccess}
          />
        )}

        <DateRangePicker />

        {/* Expanded Stats Dialog */}
        <Dialog
          open={expandedStatsDialog !== null}
          onClose={() => setExpandedStatsDialog(null)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Full Stats: {expandedStatsDialog?.blogTitle || 'Blog Stats'}
          </DialogTitle>
          <DialogContent>
            <Table size="small" sx={{ width: '100%', tableLayout: 'auto', mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Offer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">Views</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">Clicks</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">CTR</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">Sales</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">CVR</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">Revenue</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">RPV</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Overall Blog Stats Row */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 'medium' }}>content</TableCell>
                  <TableCell align="right">{expandedStatsDialog?.views?.toLocaleString() || '0'}</TableCell>
                  <TableCell align="right">{expandedStatsDialog?.clicks?.toLocaleString() || '-'}</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">{expandedStatsDialog?.sales?.toLocaleString() || '-'}</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">{expandedStatsDialog?.revenue ? `$${expandedStatsDialog.revenue.toFixed(2)}` : '-'}</TableCell>
                  <TableCell align="right">-</TableCell>
                </TableRow>
                {/* Offer-level stats rows */}
                {expandedStatsDialog?.offerStats?.map((offer, offerIndex) => (
                  <TableRow key={offerIndex}>
                    <TableCell sx={{ fontWeight: 'medium' }}>{offer.offerName}</TableCell>
                    <TableCell align="right">{offer.views.toLocaleString()}</TableCell>
                    <TableCell align="right">{offer.clicks.toLocaleString()}</TableCell>
                    <TableCell align="right">{offer.ctr}%</TableCell>
                    <TableCell align="right">{offer.sales.toLocaleString()}</TableCell>
                    <TableCell align="right">{offer.cvr}%</TableCell>
                    <TableCell align="right">${offer.revenue.toFixed(2)}</TableCell>
                    <TableCell align="right">${offer.rpv}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExpandedStatsDialog(null)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
    </BlogContext.Provider>
  );
};

export default Blog; 